import { apiPaths, customerApiBase } from "enum/paths.enum";
import { ISalesAccount } from "models/salesAccount/salesAccount.model";
import { fetchAll, fetchSingle } from "services/get";
import { getApiBaseUrl } from "services/api";
import { post } from "services/post";
import {
  CreateEcartIntegrationRequestDto,
  CreateIntegrationRequestDto,
} from "./dtos/create-integration.request.dto";
import { PatchIntegrationRequestDto } from "./dtos/patch-integration.request.dto";
import {
  FetchEcartResponseDto,
  FetchIntegrationsResponseDto,
  FetchShopifyResponseDto,
} from "./dtos/fetch-integrations.response.dto";
import { FetchSingleIntegrationCredentialsDto } from "./dtos/fetch-single-integration.response.dto";
import { patch } from "services/patch";
import { put } from "services/put";
import {
  EbayIntegrationBody,
  IIntegration,
  ISalesAccountIntegration,
  ShipstationIntegrationBody,
} from "models/integration/integration.model";

export const fetchIntegrations = async (
  salesAccountUUID: ISalesAccount["uuid"]
): Promise<FetchIntegrationsResponseDto> => {
  return await fetchAll(
    customerApiBase + "/" + salesAccountUUID + apiPaths.integrations
  );
};

export const fetchIntegrationBySlug = async (
  salesAccountUUID: ISalesAccount["uuid"],
  slug: string
): Promise<FetchSingleIntegrationCredentialsDto> => {
  return await fetchAll(
    customerApiBase +
    "/" +
    salesAccountUUID +
    apiPaths.integrations +
    "/" +
    slug
  );
};

export const createIntegration = async (
  salesAccountUUID: ISalesAccount["uuid"],
  values: CreateIntegrationRequestDto
): Promise<FetchIntegrationsResponseDto> => {
  return await post(
    customerApiBase +
    "/" +
    salesAccountUUID +
    apiPaths.integrations +
    "/create-integration",
    values
  );
};

export const createEcartIntegration = async (
  salesAccountUUID: ISalesAccount["uuid"],
  values: CreateEcartIntegrationRequestDto
): Promise<FetchEcartResponseDto> => {
  return await post(
    customerApiBase +
    "/" +
    salesAccountUUID +
    apiPaths.integrations +
    "/post/ecart",
    values
  );
};

export const fetchActiveEcartIntegrations = async (
  salesAccountUUID: ISalesAccount["uuid"]
): Promise<any> => {
  return await fetchAll(
    customerApiBase +
    "/" +
    salesAccountUUID +
    apiPaths.integrations +
    "/get/ecart"
  );
};

export const updateIntegration = async (
  integrationAccount: ISalesAccountIntegration,
  values: PatchIntegrationRequestDto,
  salesAccountUUID: ISalesAccount["uuid"]
): Promise<FetchShopifyResponseDto> => {
  console.log(values);
  return await patch(
    customerApiBase +
    "/" +
    salesAccountUUID +
    apiPaths.integrations +
    "/" +
    integrationAccount.id,
    values
  );
};

export const updateIntegrationPreferences = async (
  salesAccountUUID: ISalesAccount["uuid"],
  values: { hideInactiveConnections: boolean }
) => {
  return await put<{ hideInactiveConnections: boolean }>(
    `${customerApiBase}/${salesAccountUUID}${apiPaths.integrations}/preferences`,
    values
  );
};

export const setShipstationIntergrationStatus = async (
  salesAccountUUID: ISalesAccount["uuid"],
  values: IIntegration
): Promise<FetchShopifyResponseDto> => {
  return await post(
    customerApiBase +
    "/" +
    salesAccountUUID +
    apiPaths.integrations +
    "/shipstation/status",
    values
  );
};

export const setEbayIntergrationStatus = async (
  salesAccountUUID: ISalesAccount["uuid"],
  values: IIntegration
): Promise<FetchShopifyResponseDto> => {
  return await post(
    customerApiBase +
    "/" +
    salesAccountUUID +
    apiPaths.integrations +
    "/ebay/status",
    values
  );
};

export const createShipstationIntergration = async (
  salesAccountUUID: ISalesAccount["uuid"],
  values: ShipstationIntegrationBody
): Promise<FetchShopifyResponseDto> => {
  return await post(
    customerApiBase +
    "/" +
    salesAccountUUID +
    apiPaths.integrations +
    "/shipstation/create",
    values
  );
};

export const createEbayIntergration = async (
  salesAccountUUID: ISalesAccount["uuid"],
  values: EbayIntegrationBody
): Promise<FetchShopifyResponseDto> => {
  return await post(
    customerApiBase +
    "/" +
    salesAccountUUID +
    apiPaths.integrations +
    "/ebay/create",
    values
  );
};

export const fetchOrders = async (
  salesAccountUUID: ISalesAccount["uuid"],
  storeUUID: string
): Promise<{ statusCode: number; message: string; error: boolean }> => {
  const { statusCode, message, error } = await fetchSingle(
    `${customerApiBase}/${salesAccountUUID}${apiPaths.integrations}/fetch-orders/${storeUUID}`
  );
  return { statusCode, message, error };
};

export const triggerShipstationManualPull = async (
  salesAccountUUID: ISalesAccount["uuid"],
  salesAccountId: number
): Promise<{ statusCode: number; message?: string; error: boolean }> => {
  const url = `${getApiBaseUrl()}${customerApiBase}/${salesAccountUUID}${apiPaths.integrations
    }/shipstation/trigger-run/${salesAccountId}`;

  try {
    const response = await fetch(url, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        authorization: "Bearer " + (window as any)["accessToken"],
      },
    });

    if (!response.ok) {
      // Backend may return JSON (Nest default) or plain text.
      const bodyText = await response.text().catch(() => "");
      let message: string | undefined = bodyText;
      try {
        const parsed = bodyText ? JSON.parse(bodyText) : undefined;
        message = parsed?.message ?? message;
      } catch {
        // ignore
      }
      return { error: true, statusCode: response.status, message };
    }

    return { error: false, statusCode: response.status };
  } catch (e) {
    return { error: true, statusCode: 0, message: String(e) };
  }
};

export const redirectToEbay = async (body: {
  ID?: number;
  UserEmail: string;
  Username: string;
  SalesAccountID: number;
}) => {
  // Encode user data directly in the state parameter
  const userDataString = JSON.stringify({ ...body, Username: body.Username.trim() });
  const state = btoa(userDataString); // Use btoa for base64 encoding in browser

  // Redirect directly to eBay with the encoded state
  window.location.replace(
    process.env.NEXT_PUBLIC_VERCEL_ENV === "production"
      ? `https://auth.ebay.com/oauth2/authorize?client_id=INTERDEL-staging-PRD-3ce29bdbc-4a646e29&prompt=login&response_type=code&redirect_uri=INTERDELTA_LTD-INTERDEL-stagin-mlhpin&state=${encodeURIComponent(
        state
      )}&scope=https://api.ebay.com/oauth/api_scope https://api.ebay.com/oauth/api_scope/sell.marketing.readonly https://api.ebay.com/oauth/api_scope/sell.marketing https://api.ebay.com/oauth/api_scope/sell.inventory.readonly https://api.ebay.com/oauth/api_scope/sell.inventory https://api.ebay.com/oauth/api_scope/sell.account.readonly https://api.ebay.com/oauth/api_scope/sell.account https://api.ebay.com/oauth/api_scope/sell.fulfillment.readonly https://api.ebay.com/oauth/api_scope/sell.fulfillment https://api.ebay.com/oauth/api_scope/sell.analytics.readonly https://api.ebay.com/oauth/api_scope/sell.finances https://api.ebay.com/oauth/api_scope/sell.payment.dispute https://api.ebay.com/oauth/api_scope/commerce.identity.readonly https://api.ebay.com/oauth/api_scope/sell.reputation https://api.ebay.com/oauth/api_scope/sell.reputation.readonly https://api.ebay.com/oauth/api_scope/commerce.notification.subscription https://api.ebay.com/oauth/api_scope/commerce.notification.subscription.readonly https://api.ebay.com/oauth/api_scope/sell.stores https://api.ebay.com/oauth/api_scope/sell.stores.readonly`
      : `https://auth.sandbox.ebay.com/oauth2/authorize?client_id=INTERDEL-staging-SBX-9757afe53-c25b26b0&prompt=login&response_type=code&redirect_uri=INTERDELTA_LTD-INTERDEL-stagin-gfmcmox&state=${encodeURIComponent(
        state
      )}&scope=https://api.ebay.com/oauth/api_scope https://api.ebay.com/oauth/api_scope/buy.order.readonly https://api.ebay.com/oauth/api_scope/buy.guest.order https://api.ebay.com/oauth/api_scope/sell.marketing.readonly https://api.ebay.com/oauth/api_scope/sell.marketing https://api.ebay.com/oauth/api_scope/sell.inventory.readonly https://api.ebay.com/oauth/api_scope/sell.inventory https://api.ebay.com/oauth/api_scope/sell.account.readonly https://api.ebay.com/oauth/api_scope/sell.account https://api.ebay.com/oauth/api_scope/sell.fulfillment.readonly https://api.ebay.com/oauth/api_scope/sell.fulfillment https://api.ebay.com/oauth/api_scope/sell.analytics.readonly https://api.ebay.com/oauth/api_scope/sell.marketplace.insights.readonly https://api.ebay.com/oauth/api_scope/commerce.catalog.readonly https://api.ebay.com/oauth/api_scope/buy.shopping.cart https://api.ebay.com/oauth/api_scope/buy.offer.auction https://api.ebay.com/oauth/api_scope/commerce.identity.readonly https://api.ebay.com/oauth/api_scope/commerce.identity.email.readonly https://api.ebay.com/oauth/api_scope/commerce.identity.phone.readonly https://api.ebay.com/oauth/api_scope/commerce.identity.address.readonly https://api.ebay.com/oauth/api_scope/commerce.identity.name.readonly https://api.ebay.com/oauth/api_scope/commerce.identity.status.readonly https://api.ebay.com/oauth/api_scope/sell.finances https://api.ebay.com/oauth/api_scope/sell.payment.dispute https://api.ebay.com/oauth/api_scope/sell.item.draft https://api.ebay.com/oauth/api_scope/sell.item https://api.ebay.com/oauth/api_scope/sell.reputation https://api.ebay.com/oauth/api_scope/sell.reputation.readonly https://api.ebay.com/oauth/api_scope/commerce.notification.subscription https://api.ebay.com/oauth/api_scope/commerce.notification.subscription.readonly https://api.ebay.com/oauth/api_scope/sell.stores https://api.ebay.com/oauth/api_scope/sell.stores.readonly`
  );
};

// Temu OAuth (EU Seller Open Platform).
// Docs: https://partner-eu.temu.com/documentation
// app_key + redirect_uri come from the Temu app set up under the partner console.
// State is a base64-encoded JSON of { salesAccountUUID, name, useDefaultDimensions }
// so the callback can attach the granted token to the right account, persist the
// user-supplied name, and record how Temu parcels should be sized.
export const redirectToTemu = async ({
  salesAccountUUID,
  name,
  useDefaultDimensions,
}: {
  salesAccountUUID: string;
  name: string;
  useDefaultDimensions: boolean;
}) => {
  // Same approved app for production and staging, so a single key.
  const appKey = process.env.NEXT_PUBLIC_TEMU_APP_KEY;

  const connexxEnv =
    process.env.NEXT_PUBLIC_VERCEL_ENV === "production"
      ? "https://api.connexx.co.uk"
      : "https://nest-staging-53oj.onrender.com";

  const redirectUri = `${connexxEnv}/temu/callback`;

  const state = btoa(
    JSON.stringify({ salesAccountUUID, name, useDefaultDimensions })
  );

  // Region matters: the app is registered on the EU open platform, and Temu
  // rejects authorization started from another region's seller portal with
  // "store region doesn't match your app configuration".
  window.location.replace(
    `https://seller-eu.temu.com/open-platform/client-manage/authorization?appKey=${appKey}&redirectUri=${encodeURIComponent(
      redirectUri
    )}&state=${encodeURIComponent(state)}`
  );
};

// Public-app installs must start from Shopify (App Store requirement 2.3.1),
// so Connexx never builds the authorize URL any more. This is the final step
// of that flow: attach the freshly installed shop to the caller's account.
export const linkShopifyStore = async ({
  linkToken,
}: {
  linkToken: string;
}) => {
  return await post<{ shopDomain: string; alreadyLinked: boolean }>(
    "/shopify/link",
    { linkToken }
  );
};

export const sendToCustomShopify = async ({
  shopDomain,
  salesAccountID,
  clientId,
}: {
  shopDomain: string;
  salesAccountID: number;
  clientId: string;
}) => {
  const connexxEnv = process.env.NEXT_PUBLIC_VERCEL_ENV === "production" ? "https://api.connexx.co.uk" : "http://localhost:3002";
  window.location.replace(`https://${shopDomain}/admin/oauth/authorize?client_id=${clientId}&scope=read_assigned_fulfillment_orders,write_assigned_fulfillment_orders,read_fulfillments,write_fulfillments,read_inventory,read_merchant_managed_fulfillment_orders,write_merchant_managed_fulfillment_orders,read_orders,write_orders,read_product_listings,write_product_listings,read_products&redirect_uri=${connexxEnv}/shopify/custom-callback&state=${salesAccountID}`)
};